import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { useI18n } from "@wato/i18n/react";
import type { MessageKey } from "@wato/i18n";
import { HelpPopover } from "@wato/ui";
import { useApi } from "../../api/context";
import { ApiErrorState } from "../../components/ApiErrorState";
import { KpiSkeleton, WindowToggle } from "../../components/analytics/cards";
import { PeriodLabel } from "../../components/analytics/PeriodLabel";
import { lastDaysRange } from "../../lib/analytics-range";
import { workspaceCurrency } from "../../lib/format-minor";
import type { OverviewPeriod } from "../../api/overview-types";
import { ChannelChips } from "../overview/ChannelChips";
import { OverviewCharts } from "../overview/OverviewCharts";
import { TeamTable, TopLabels, TopProducts } from "../overview/OverviewTables";
import { hasAnySection, visibleSections } from "../overview/overview-model";
import { useWorkspace } from "../workspace/WorkspaceContext";

const PERIODS: readonly { readonly value: OverviewPeriod; readonly label: MessageKey }[] = [
  { value: "today", label: "dashboard.overview.period.today" },
  { value: "7d", label: "dashboard.overview.period.week" },
  { value: "30d", label: "dashboard.overview.period.month" },
];

/**
 * OWN-319 «التقارير»: the charts and tables of the old overview page, at the end of Home.
 * One request (`GET .../overview?period=`); the API leaves out every section the member may not see,
 * and so does this block (no empty shells). `/overview` redirects to `#reports`.
 */
export function HomeReports() {
  const { t } = useI18n();
  const { workspace } = useWorkspace();
  const { hash } = useLocation();
  const [period, setPeriod] = useState<OverviewPeriod>("today");
  const api = useApi();
  const query = useQuery({
    queryKey: ["overview", workspace.id, period],
    queryFn: () => api.getOverview(workspace.id, period),
    staleTime: 60_000,
  });
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (hash === "#reports") ref.current?.scrollIntoView?.({ block: "start" });
  }, [hash]);

  const data = query.data;
  const show = data ? visibleSections(data) : null;
  const currency =
    data?.sales?.currency ??
    data?.wallet?.currency ??
    data?.abandonedCarts?.currency ??
    workspaceCurrency(workspace.country);
  // Nothing to report for this role/period: no heading either.
  if (data && show && !hasAnySection(show)) return null;

  return (
    <section
      id="reports"
      ref={ref}
      aria-label={t("dashboard.b29b.home.reports")}
      data-testid="home-reports"
      className="flex min-w-0 scroll-mt-4 flex-col gap-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 flex items-center gap-2 text-base font-semibold text-fg">
          {t("dashboard.b29b.home.reports")}
          <HelpPopover>{t("dashboard.overview.subtitle")}</HelpPopover>
        </h3>
        <WindowToggle
          label={t("dashboard.overview.period.label")}
          options={PERIODS.map((p) => ({ value: p.value, label: t(p.label) }))}
          value={period}
          onChange={setPeriod}
        />
      </div>
      {period === "today" ? null : (
        <PeriodLabel {...lastDaysRange(period === "7d" ? 7 : 30)} days={period === "7d" ? 7 : 30} />
      )}
      {query.isPending ? <KpiSkeleton count={4} /> : null}
      {query.isError ? (
        <ApiErrorState
          error={query.error}
          onRetry={() => void query.refetch()}
          workspaceId={workspace.id}
        />
      ) : null}
      {data && show ? (
        <>
          {data.charts && (show.conversationsChart || show.salesChart || show.busyHours) ? (
            <OverviewCharts charts={data.charts} currency={currency} show={show} />
          ) : null}
          {show.team && data.team ? <TeamTable team={data.team} /> : null}
          {(show.topProducts && data.topProducts) || (show.topLabels && data.topLabels) ? (
            <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
              {show.topProducts && data.topProducts ? (
                <div className="min-w-0">
                  <TopProducts products={data.topProducts} currency={currency} />
                </div>
              ) : null}
              {show.topLabels && data.topLabels ? (
                <div className="min-w-0">
                  <TopLabels labels={data.topLabels} />
                </div>
              ) : null}
            </div>
          ) : null}
          {show.channels && data.channels ? (
            <ChannelChips workspaceId={workspace.id} channels={data.channels} />
          ) : null}
        </>
      ) : null}
    </section>
  );
}
